import React from 'react';
import { Shirt, Box, LayoutGrid, Sparkles } from 'lucide-react';
import img1 from "../../assets/wardrobe1.jpg";
import img2 from "../../assets/wardrobe2.jpg";
import img3 from "../../assets/wardrobe3.jpg";
import img4 from "../../assets/wardrobe4.jpg";
import img5 from "../../assets/wardrobe5.jpg";

// Wardrobe Data
const designs = [
  { 
    id: 1, 
    title: 'Luxury Walk-In', 
    category: 'Premium', 
    image: img1 
  },
  { 
    id: 2, 
    title: 'Modern Sliding Glass', 
    category: 'Modern', 
    image: img2 
  },
  { 
    id: 3, 
    title: 'Classic Wood Finish', 
    category: 'Classic', 
    image: img3
  },
  { 
    id: 4, 
    title: 'Minimalist Open Concept', 
    category: 'Contemporary', 
    image: img4 
  },
];

const Wardrobe = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* 1. Hero Section */}
     <div
        className="relative h-[60vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h4 className="text-amber-500 font-bold tracking-widest uppercase mb-3">Organized Living</h4>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-4">
            Custom Wardrobes
          </h1>
          <p className="text-gray-200 text-lg max-w-xl">
            Where style meets storage. Perfectly tailored to your lifestyle.
          </p>
        </div>
      </div>

      {/* 2. Intro Section */}
      <section className="py-16 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
          The Art of Organization
        </h2>
        <div className="w-20 h-1 bg-amber-500 mx-auto mb-6"></div>
        <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
          A wardrobe is not just for storage; it's a showcase for your personal style. 
          At <span className="font-bold text-gray-900">Luxuriya Homes</span>, we design intelligent storage solutions 
          that maximize space while adding a touch of glamour to your bedroom.
        </p>
      </section>

      {/* 3. Design Gallery */}
      <section className="py-16 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 text-center md:text-left">
            <h3 className="text-3xl font-serif font-bold text-gray-900">Signature Styles</h3>
            <p className="text-gray-500 mt-1">Explore our range of closet designs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden shadow-lg cursor-pointer rounded-sm">
                {/* Image */}
                <div className="h-80 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={design.title} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700 ease-in-out"
                  />
                </div>
                {/* Text Overlay */}
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 to-transparent p-6 pt-20">
                  <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">{design.category}</p>
                  <h4 className="text-2xl font-serif text-white">{design.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Features Section */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="flex flex-col items-center">
            <div className="mb-4 text-amber-500 p-3 bg-white/10 rounded-full">
              <LayoutGrid className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-2">Smart Layouts</h4>
            <p className="text-gray-400 text-sm">Custom compartments for shoes, accessories, and long coats.</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="mb-4 text-amber-500 p-3 bg-white/10 rounded-full">
              <Sparkles className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-2">Premium Finishes</h4>
            <p className="text-gray-400 text-sm">High-gloss laminates, tinted glass, and soft-close mechanisms.</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="mb-4 text-amber-500 p-3 bg-white/10 rounded-full">
              <Shirt className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-2">Walk-In Luxury</h4>
            <p className="text-gray-400 text-sm">Spacious walk-in closets designed with island counters and lighting.</p>
          </div>
        </div>
      </section>

      {/* 5. CTA Section */}
      <section className="py-20 px-6 text-center bg-white">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">
          Upgrade Your Storage Today
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          From compact sliding wardrobes to expansive walk-in closets, we build it all.
        </p>
        <button className="bg-amber-500 hover:bg-amber-600 text-white text-lg font-semibold px-10 py-3 rounded-sm transition shadow-xl">
          Get a Free Quote
        </button>
      </section>
    </div>
  );
};

export default Wardrobe;